import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import api from '@/axios';
import {
  GET_PURCHASE_ORDER_LINES_KEY,
  GET_PURCHASE_ORDER_TOTALS_KEY,
  SYNC_PURCHASE_ORDER_LINES_KEY,
} from '@/keys';

export type SyncPurchaseOrderLinesParams = {
  id: string;
  productIds: number[] | null;
  syncFieldKeys: string[];
};

export type SyncPurchaseOrderLinesResult = {
  skippedProductHsCodeLines: { lineId: number; sku: number | null }[];
};

const syncPurchaseOrderLines = async ({
  id,
  productIds,
  syncFieldKeys,
}: SyncPurchaseOrderLinesParams): Promise<
  SyncPurchaseOrderLinesResult | undefined
> => {
  const response = await api.post<SyncPurchaseOrderLinesResult>(
    `/purchase-orders/${id}/sync-lines`,
    {
      productIds,
      syncFieldKeys,
    },
  );

  return response.status === 204 ? undefined : response.data;
};

export const useSyncPurchaseOrderLines = (): UseMutationResult<
  SyncPurchaseOrderLinesResult | undefined,
  Error,
  SyncPurchaseOrderLinesParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: SYNC_PURCHASE_ORDER_LINES_KEY,
    mutationFn: syncPurchaseOrderLines,
    onSuccess: async (_data, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...GET_PURCHASE_ORDER_LINES_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [...GET_PURCHASE_ORDER_TOTALS_KEY, id],
        }),
      ]);
    },
  });
};
